import { Select } from 'ol/interaction';
import { Feature } from 'ol';
import { createEvent } from '@/primary/webmapping/common/event/createEvent';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import BaseEvent from 'ol/events/Event';

const onFeatureChange = (select: OLClickSelect, filter: (feature: Feature) => boolean) => (event: BaseEvent) => {
  const feature = event.target;
  if (!filter(feature)) {
    select.select(null);
  }
};

export interface SelectEvent {
  deselected: Feature[];
  selected: Feature[];
}

export class OLClickSelect {
  private selectInteraction!: Select;
  private layerSource!: VectorSource;
  private selectionSource!: VectorSource;
  private filter!: (feature: Feature) => boolean;
  private callBack!: (event: BaseEvent) => void;

  constructor(select: Select, layer: VectorLayer, selectionLayer: VectorLayer, filter: (feature: Feature) => boolean) {
    this.selectInteraction = select;
    this.layerSource = layer.getSource();
    this.selectionSource = selectionLayer.getSource();
    this.filter = filter;
    this.selectInteraction.on('select', event => this.handleSelect(event));
  }

  public getFeatureId(): string | null {
    const feature = this.selectInteraction.getFeatures().item(0);
    return feature ? feature.getId().toString() : null;
  }

  private handleSelect(event: SelectEvent) {
    if (event.deselected[0]) {
      this.selectionSource.clear();
      const feature = event.deselected[0];
      feature.un('propertychange', this.callBack);
    }
    if (event.selected[0]) {
      const feature = event.selected[0];
      const clone = feature.clone();
      clone.setStyle(undefined);
      this.selectionSource.addFeature(clone);
      this.callBack = onFeatureChange(this, this.filter);
      feature.on('propertychange', this.callBack);
      this.selectInteraction.dispatchEvent(createEvent('select:feature', { id: feature.getId().toString() }));
    }
    if (event.deselected[0] && !event.selected[0]) {
      this.selectInteraction.dispatchEvent(createEvent('select:feature', { id: null }));
    }
  }

  public onSelect(callable: (id: string | null) => void) {
    this.selectInteraction.on('select:feature', event => callable(event.id));
  }

  public select(featureId: string | null, silent = false) {
    const selectedFeature = this.layerSource.getFeatureById(featureId || '');
    const deselectedFeature = this.selectInteraction.getFeatures().item(0);

    if (selectedFeature) {
      this.selectInteraction.getFeatures().setAt(0, selectedFeature);
    }
    if (selectedFeature === null) {
      this.selectInteraction.getFeatures().clear();
    }

    if (!silent) {
      const selected = selectedFeature ? [selectedFeature] : [];
      const deselected = deselectedFeature ? [deselectedFeature] : [];
      this.selectInteraction.dispatchEvent(createEvent('select', { selected, deselected }));
    }
  }
}
